'use strict';

var _ = require('underscore'),
    getScrollY = require('../utils/getScrollY'),
    SectionTypes = require('../constants/SectionTypes'),
    { isMobile, isIE10 } = require('../utils/Browser'),
    firstIfShallowEqual = require('../utils/firstIfShallowEqual');

var OFFSCREEN_FADING = {
  isActive: false,
  isVisible: false,
  topOpacity: 0,
  bottomOpacity: 0
};

function isPageVisible() {
  var hidden = document.hidden;
  if (!_.isBoolean(hidden)) {
    hidden = false;
  }

  return !hidden;
}

function interpolate(val, points) {
  if (points[0] === points[1]) {
    return points[0];
  }

  var result = (val - points[0]) / (points[1] - points[0]),
      precision = 1 / (window.safari ? 0.1 : 0.02);

  result = Math.min(1, Math.max(0, result));
  result = Math.round(result * precision) / precision;
  return result;
}

/**
 * Keeps track of fading for posters as user scrolls the window.
 * Expects `getSectionNode(i)` to be defined.
 */
var FadingCalculatorMixin = {
  getInitialState() {
    return {
      posterFadings: null
    };
  },

  isFadingEnabled() {
    // On IE10, we don't have pointer-events
    // and fades make some buttons unclickable.
    return !isIE10() && !isMobile();
  },

  /**
   * Returns an object with following properties:
   *
   * - isActive: whether a poster is active at the moment
   * - top: top fading opacity, 0 to 1
   * - bottom: bottom fading opacity, 0 to 1
   *
   * @param {number} index Index of the poster.
   */
  getSectionFading(index) {
    if (!this.isFadingEnabled()) {
      return;
    }

    var sections = this.state.sections || [],
        fadings = this.state.posterFadings || [],
        type = sections[index].type;

    if (type === SectionTypes.POSTER || type === SectionTypes.COVER) {
      return fadings[index] || OFFSCREEN_FADING;
    }
  },

  componentDidMount() {
    if (!this.isFadingEnabled()) {
      return;
    }

    this._sectionHeights = [];
    this._updateSectionHeights();

    window.addEventListener('scroll', this._updatePosterFadings);
    window.addEventListener('visibilitychange', this._updateSectionHeights);
    window.addEventListener('resize', this._updateSectionHeights);
    this._updateHeightsInterval = setInterval(this._updateSectionHeights, 700);
  },

  componentWillUnmount() {
    if (!this.isFadingEnabled()) {
      return;
    }

    clearInterval(this._updateHeightsInterval);
    window.removeEventListener('visibilitychange', this._updateSectionHeights);
    window.removeEventListener('resize', this._updateSectionHeights);
    window.removeEventListener('scroll', this._updatePosterFadings);
  },

  componentDidUpdate(prevProps, prevState) {
    if (!this.isFadingEnabled()) {
      return;
    }

    if (this.state.sections !== prevState.sections) {
      this._updateSectionHeights();
    }
  },

  _updateSectionHeights() {
    if (!this.isMounted()) {
      return;
    }

    if (!isPageVisible()) {
      return;
    }

    var count = this.state.sections.length,
        node,
        offsetHeight,
        didChange;

    if (this._sectionHeights.length !== count) {
      this._sectionHeights.length = count;
      didChange = true;
    }

    for (var i = 0; i < count; i++) {
      node = this.getSectionNode(i);
      offsetHeight = node.offsetHeight;

      if (offsetHeight !== this._sectionHeights[i]) {
        this._sectionHeights[i] = offsetHeight;
        didChange = true;
      }
    }

    if (didChange) {
      this._updatePosterFadings();
    }
  },

  _updatePosterFadings() {
    if (!this.isMounted()) {
      return;
    }

    var scrollY = getScrollY(),
        sectionHeights = this._sectionHeights,
        boundingRects = this._getBoundingRects(sectionHeights, scrollY),
        state = this._getFadingState(boundingRects);

    this.setState(state);
  },

  /**
   * Calculates active poster index and fading.
   *
   * @param {array<object>} boundingRects Bounding rects for each section.
   */
  _getFadingState(boundingRects) {
    var prevPosterFadings = this.state.posterFadings,
        viewportHeight = window.innerHeight,
        sectionCount = this.state.sections.length,
        visiblePosters = this._findVisiblePosters(boundingRects, viewportHeight),
        activePoster = this._findActivePoster(visiblePosters, viewportHeight),
        posterFadings = null;

    if (visiblePosters.length) {
      posterFadings = new Array(sectionCount);
    }

    for (var visibleIndex = 0; visibleIndex < visiblePosters.length; visibleIndex++) {
      var poster = visiblePosters[visibleIndex],
          posterIndex = poster.index,
          rect = poster.rect,
          fading;

      if (!rect.height) {
        continue;
      }

      fading = this._getVisiblePosterFading(
        posterIndex, rect, (poster === activePoster),
        boundingRects, viewportHeight
      );

      // Avoid updating object if all its values match the previous fading
      // so that the component can benefit from reference equality check on fading.

      if (prevPosterFadings) {
        fading = firstIfShallowEqual(prevPosterFadings[posterIndex], fading);
      }

      posterFadings[posterIndex] = fading;
    }

    // If *all* values in array are the same, avoid changes.
    posterFadings = firstIfShallowEqual(prevPosterFadings, posterFadings);

    return {
      posterFadings: posterFadings
    };
  },

  _getVisiblePosterFading(index, rect, isActive, boundingRects, viewportHeight) {
    if (!rect.height) {
      return;
    }

    var fading = {
      isVisible: true,
      isActive: isActive,
      progress: this._getPosterProgress(rect, viewportHeight),
      topOpacity: 0,
      bottomOpacity: 0
    };

    if (isActive) {
      var isTall = rect.height > viewportHeight,
          isCover = this.state.sections[index].type === SectionTypes.COVER,
          isBeforeInfo = this.state.sections[index + 1].type === SectionTypes.INFO,
          posterDistances = this._getDistanceToPosters(index, boundingRects),
          opacities;

      opacities = (isTall || isCover) ?
        this._getTallPosterOpacities(rect, viewportHeight, posterDistances) :
        this._getShortPosterOpacities(rect, viewportHeight, posterDistances);

      if (isBeforeInfo) {
        opacities.bottom = 0;
      }

      fading.topOpacity = opacities.top;
      fading.bottomOpacity = opacities.bottom;
    }

    return fading;
  },

  _getDistanceToPosters(index, boundingRects) {
    var distanceToPrev = 0,
        distanceToNext = 0,
        sections = this.state.sections,
        i;

    for (i = index - 1; i >= 0; i--) {
      if (sections[i].type === SectionTypes.POSTER) {
        break;
      }

      distanceToPrev += boundingRects[i].height;
    }

    for (i = index + 1; i < boundingRects.length; i++) {
      if (sections[i].type === SectionTypes.POSTER) {
        break;
      }

      distanceToNext += boundingRects[i].height;
    }

    return {
      next: distanceToNext,
      prev: distanceToPrev
    };
  },

  _getShortPosterOpacities(rect, viewportHeight, posterDistances) {
    var top = rect.top,
        height = rect.height,
        viewportMiddle = viewportHeight / 2,
        isTooCloseToPrevPoster = posterDistances.prev > 0 && posterDistances.prev < viewportHeight,
        isTooCloseToNextPoster = posterDistances.next > 0 && posterDistances.next < viewportHeight;

    return {
      top: interpolate(top, {
        0: (isTooCloseToPrevPoster) ?
          viewportMiddle + posterDistances.prev / 4:
          viewportHeight,

        1: viewportHeight - height
      }),
      bottom: interpolate(top, {
        1: 0,
        0: (isTooCloseToNextPoster) ?
          viewportMiddle - (rect.height + posterDistances.next / 4) :
          -viewportMiddle
      })
    };
  },

  _getTallPosterOpacities(rect, viewportHeight, posterDistances) {
    var top = rect.top,
        bottom = rect.bottom,
        fadeInEarlier = posterDistances.prev > 0 && posterDistances.prev < viewportHeight,
        fadeInTop,
        fadeInDistance;

    if (posterDistances.prev > 0) {
      fadeInTop = viewportHeight / 2;
      if (!fadeInEarlier) {
        fadeInTop += viewportHeight / 4;
      }

      fadeInDistance = viewportHeight / 2;
    } else {
      fadeInTop = viewportHeight;
      fadeInDistance = viewportHeight;
    }
    return {
      top: interpolate(top, {
        0: fadeInTop,
        1: fadeInTop - fadeInDistance
      }),
      bottom: interpolate(bottom, {
        1: viewportHeight,
        0: viewportHeight / 2
      })
    };
  },

  _getPosterProgress(rect, viewportHeight) {
    var viewportMiddle = viewportHeight / 2;

    return interpolate(rect.top, {
      0: viewportMiddle,
      1: viewportMiddle - rect.height
    });
  },

  /**
   * Finds the single active poster, if any.
   * Assumes an array of { index, section, rect }.
   *
   * To find the active poster, it takes all visible posters and finds
   * the one that either intersects viewport middle, or has
   * the minimal distance from its edge to the viewport middle.
   *
   * @param {array<object>} visiblePosters All posters at least partially visible now.
   * @param {number} viewportHeight Viewport height.
   */
  _findActivePoster(visiblePosters, viewportHeight) {
    if (!visiblePosters.length) {
      return;
    }

    var viewportMiddle = viewportHeight / 2;
    return _.min(visiblePosters, function (poster) {
      var rect = poster.rect;
      if (rect.top <= viewportMiddle && rect.bottom >= viewportMiddle) {
        return 0;
      }

      return Math.min(
        Math.abs(rect.top - viewportMiddle),
        Math.abs(rect.bottom - viewportMiddle)
      );
    });
  },

  /**
   * Finds posters that at least partially intersect the viewport.
   * For matching posters, returns { index, section, rect }.
   *
   * @param {array<object>} boundingRects Bounding rects for each section.
   * @param {number} viewportHeight Viewport height.
   */
  _findVisiblePosters(boundingRects, viewportHeight) {
    var sections = this.state.sections,
        visiblePosters = [],
        section,
        rect,
        isPoster,
        isCoverBeforePoster,
        i;

    for (i = 0; i < sections.length; i++) {
      if (!boundingRects[i]) {
        continue;
      }

      section = sections[i];
      rect = boundingRects[i];

      if (rect.bottom <= 0 || rect.top >= viewportHeight || rect.height === 0) {
        continue;
      }

      isPoster = section.type === SectionTypes.POSTER;
      isCoverBeforePoster = i === 0 && sections.length > 1 && sections[1].type === SectionTypes.POSTER;

      if (isPoster || isCoverBeforePoster) {
        visiblePosters.push({
          index: i,
          section: section,
          rect: rect
        });
      }
    }

    return visiblePosters;
  },

  /**
   * Makes a guess at section bounding rects based on their heights and scroll position.
   *
   * This method doesn't actually call `getBoundingClientRect` for performance
   * reasons so it will break if there are margins between sections.
   *
   * @param {array<number>} sectionHeights Heights for each section.
   * @param {number} scrollY Current scroll position.
   */
  _getBoundingRects(sectionHeights, scrollY) {
    var accumulatedScrollY = 0,
        boundingRects = new Array(sectionHeights.length),
        height;

    for (var i = 0; i < sectionHeights.length; i++) {
      height = sectionHeights[i] || 0;

      boundingRects[i] = {
        top: accumulatedScrollY - scrollY,
        bottom: accumulatedScrollY - scrollY + height,
        height: height
      };

      accumulatedScrollY += height;
    }

    return boundingRects;
  }
};

module.exports = FadingCalculatorMixin;



/** WEBPACK FOOTER **
 ** ./mixins/FadingCalculatorMixin.js
 **/